import { useState, useRef, useEffect } from 'react'
import {
  Box,
  TextField,
  IconButton,
  InputAdornment,
  Popover,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Typography,
} from '@mui/material'
import {
  Send,
  EmojiEmotions,
  Close,
  AddPhotoAlternate,
  PhotoCamera,
  Collections,
  Image as ImageIcon,
  Link as LinkIcon,
  People,
  Map as MapIcon,
} from '@mui/icons-material'
import { chatApi } from '@api/endpoints/chat'
import { getImageUrl } from '@utils/imageUtils'
import { ChatReferenceKind, type ChatReferenceInput } from '@api/types/chat'
import { ServerImagePickerDialog } from './ServerImagePickerDialog'
import { ChatReferencePickerDialog } from './ChatReferencePickerDialog'

interface MessageInputProps {
  onSend: (
    content: string,
    imageId?: string,
    imageMaxViews?: number,
    imageViewSeconds?: number,
    references?: ChatReferenceInput[],
  ) => void
  onTyping?: (isTyping: boolean) => void
  onImageUpload?: (file: File) => Promise<string | undefined>
  disabled?: boolean
  placeholder?: string
}

// Pending attachment: either a not-yet-uploaded File (camera/local) or an already-uploaded image
// (library copy, which the server resolves to an imageId immediately).
interface PendingAttachment {
  previewUrl: string
  file?: File
  imageId?: string
}

// Common emoji set
const EMOJI_LIST = [
  '😊', '😂', '😍', '🥰', '😎', '🤔', '😅', '😢',
  '👍', '👎', '❤️', '🎉', '🔥', '⭐', '✅', '🙏',
  '💪', '🎯', '🏆', '🌟', '💯', '🚀', '⛺', '🥾',
  '🏕️', '🌲', '🌄', '🗺️', '🧭', '🎒', '⛰️', '🌅',
]

// View-limit options for ephemeral images (undefined = no limit / stays).
const VIEW_LIMIT_OPTIONS: Array<{ label: string; value: number | undefined }> = [
  { label: 'Stays', value: undefined },
  { label: '1 view', value: 1 },
  { label: '3 views', value: 3 },
  { label: '5 views', value: 5 },
]

// Max view-time options (seconds) — shown once a view limit is chosen (undefined = no time limit).
const VIEW_TIME_OPTIONS: Array<{ label: string; value: number | undefined }> = [
  { label: 'No limit', value: undefined },
  { label: '5s', value: 5 },
  { label: '15s', value: 15 },
  { label: '30s', value: 30 },
  { label: '60s', value: 60 },
]

export function MessageInput({
  onSend,
  onTyping,
  onImageUpload,
  disabled = false,
  placeholder = 'Type a message...',
}: MessageInputProps) {
  const [message, setMessage] = useState('')
  const [emojiAnchor, setEmojiAnchor] = useState<null | HTMLElement>(null)
  const [attachAnchor, setAttachAnchor] = useState<null | HTMLElement>(null)
  const [refPickerOpen, setRefPickerOpen] = useState(false)
  // Attached references plus the label the sender saw. The label drives the chip only — it is
  // never sent, because what each reader is shown is resolved server-side, per reader.
  const [references, setReferences] = useState<{ ref: ChatReferenceInput; label: string }[]>([])
  const [libraryOpen, setLibraryOpen] = useState(false)
  const [attachment, setAttachment] = useState<PendingAttachment | null>(null)
  const [imageMaxViews, setImageMaxViews] = useState<number | undefined>(undefined)
  const [imageViewSeconds, setImageViewSeconds] = useState<number | undefined>(undefined)
  const [isUploading, setIsUploading] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const cameraInputRef = useRef<HTMLInputElement>(null)
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current)
    }
  }, [])

  // While the parent's send mutation is pending, MUI disables the textarea — which blurs it. Restore
  // focus once the mutation completes so the user can immediately type the next message.
  const wasDisabledRef = useRef(disabled)
  useEffect(() => {
    if (wasDisabledRef.current && !disabled) inputRef.current?.focus()
    wasDisabledRef.current = disabled
  }, [disabled])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMessage(e.target.value)
    if (onTyping) {
      onTyping(true)
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current)
      typingTimeoutRef.current = setTimeout(() => onTyping(false), 2000)
    }
  }

  const clearAttachment = () => {
    setAttachment(null)
    setImageMaxViews(undefined)
    setImageViewSeconds(undefined)
  }

  const handleSend = async () => {
    const trimmedMessage = message.trim()
    // A message that is only a reference is a perfectly good thing to send.
    if ((!trimmedMessage && !attachment && references.length === 0) || disabled || isUploading) return

    let imageId: string | undefined

    if (attachment?.imageId) {
      // Library copy — already uploaded server-side.
      imageId = attachment.imageId
    } else if (attachment?.file && onImageUpload) {
      setIsUploading(true)
      try {
        imageId = await onImageUpload(attachment.file)
      } catch (error) {
        console.error('Failed to upload image:', error)
        setIsUploading(false)
        return
      }
      setIsUploading(false)
    }

    onSend(
      trimmedMessage || '',
      imageId,
      imageId ? imageMaxViews : undefined,
      imageId && imageMaxViews ? imageViewSeconds : undefined,
      references.length > 0 ? references.map((r) => r.ref) : undefined
    )
    setMessage('')
    clearAttachment()
    setReferences([])

    if (onTyping) {
      onTyping(false)
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current)
    }
    inputRef.current?.focus()
  }

  const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = '' // reset so re-selecting the same file fires change
    if (!file || !file.type.startsWith('image/')) return

    const reader = new FileReader()
    reader.onload = () => setAttachment({ previewUrl: reader.result as string, file })
    reader.readAsDataURL(file)
  }

  const handleLibrarySelect = async (fileId: string) => {
    setLibraryOpen(false)
    setIsUploading(true)
    try {
      const res = await chatApi.attachLibraryImage(fileId)
      setAttachment({ previewUrl: getImageUrl(res.imageUrl) ?? res.imageUrl, imageId: res.imageId })
    } catch (error) {
      console.error('Failed to attach library image:', error)
    } finally {
      setIsUploading(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleEmojiClick = (emoji: string) => {
    setMessage((prev) => prev + emoji)
    setEmojiAnchor(null)
    inputRef.current?.focus()
  }

  return (
    <Box sx={{ p: 2, backgroundColor: 'background.paper', borderTop: 1, borderColor: 'divider' }}>
      {/* Attached references, so what will be sent is visible before sending */}
      {references.length > 0 && (
        <Box sx={{ mb: 1, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {references.map(({ ref, label }) => (
            <Chip
              key={`${ref.kind}:${ref.id}`}
              size="small"
              icon={ref.kind === ChatReferenceKind.Group
                ? <People sx={{ fontSize: 14 }} />
                : <MapIcon sx={{ fontSize: 14 }} />}
              label={label}
              onDelete={() =>
                setReferences((cur) => cur.filter((c) => !(c.ref.id === ref.id && c.ref.kind === ref.kind)))
              }
              sx={{ maxWidth: 220 }}
            />
          ))}
        </Box>
      )}

      {/* Attachment preview + view-limit selector */}
      {attachment && (
        <Box sx={{ mb: 1, display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
          <Box sx={{ position: 'relative', flexShrink: 0 }}>
            <img
              src={attachment.previewUrl}
              alt="Preview"
              style={{ maxWidth: 120, maxHeight: 120, borderRadius: 8, objectFit: 'cover', display: 'block' }}
            />
            <IconButton
              size="small"
              onClick={clearAttachment}
              sx={{
                position: 'absolute', top: -8, right: -8,
                backgroundColor: 'background.paper', boxShadow: 1,
                '&:hover': { backgroundColor: 'error.light', color: 'white' },
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Disappears after
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {VIEW_LIMIT_OPTIONS.map((opt) => (
                <Chip
                  key={opt.label}
                  label={opt.label}
                  size="small"
                  color={imageMaxViews === opt.value ? 'primary' : 'default'}
                  variant={imageMaxViews === opt.value ? 'filled' : 'outlined'}
                  onClick={() => setImageMaxViews(opt.value)}
                />
              ))}
            </Box>

            {/* Max view time — only relevant for a view-limited image. */}
            {imageMaxViews != null && (
              <>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, mb: 0.5 }}>
                  Max view time
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {VIEW_TIME_OPTIONS.map((opt) => (
                    <Chip
                      key={opt.label}
                      label={opt.label}
                      size="small"
                      color={imageViewSeconds === opt.value ? 'primary' : 'default'}
                      variant={imageViewSeconds === opt.value ? 'filled' : 'outlined'}
                      onClick={() => setImageViewSeconds(opt.value)}
                    />
                  ))}
                </Box>
              </>
            )}
          </Box>
        </Box>
      )}

      {/* Hidden inputs */}
      <input type="file" ref={fileInputRef} onChange={handleFileSelected} accept="image/*" style={{ display: 'none' }} />
      <input type="file" ref={cameraInputRef} onChange={handleFileSelected} accept="image/*" capture="environment" style={{ display: 'none' }} />

      <TextField
        fullWidth
        multiline
        maxRows={4}
        value={message}
        onChange={handleChange}
        onKeyPress={handleKeyPress}
        placeholder={placeholder}
        disabled={disabled || isUploading}
        inputRef={inputRef}
        InputProps={{
          sx: { borderRadius: 3, bgcolor: 'action.hover' },
          startAdornment: (
            <InputAdornment position="start">
              <IconButton size="small" onClick={(e) => setEmojiAnchor(e.currentTarget)} disabled={disabled || isUploading}>
                <EmojiEmotions fontSize="small" />
              </IconButton>
              {onImageUpload && (
                <IconButton
                  size="small"
                  onClick={(e) => setAttachAnchor(e.currentTarget)}
                  disabled={disabled || isUploading}
                  aria-label="Attach image"
                >
                  <AddPhotoAlternate fontSize="small" />
                </IconButton>
              )}
              <IconButton
                size="small"
                onClick={() => setRefPickerOpen(true)}
                disabled={disabled || isUploading}
                aria-label="Reference an adventure or group"
              >
                <LinkIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              {isUploading ? (
                <CircularProgress size={24} />
              ) : (
                <IconButton
                  onClick={handleSend}
                  disabled={(!message.trim() && !attachment && references.length === 0) || disabled}
                  color="primary"
                >
                  <Send />
                </IconButton>
              )}
            </InputAdornment>
          ),
        }}
      />

      {/* Attach source menu */}
      <Menu anchorEl={attachAnchor} open={Boolean(attachAnchor)} onClose={() => setAttachAnchor(null)}>
        <MenuItem
          onClick={() => { setAttachAnchor(null); cameraInputRef.current?.click() }}
        >
          <ListItemIcon><PhotoCamera fontSize="small" /></ListItemIcon>
          <ListItemText>Take photo</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => { setAttachAnchor(null); fileInputRef.current?.click() }}
        >
          <ListItemIcon><ImageIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Upload image</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => { setAttachAnchor(null); setLibraryOpen(true) }}
        >
          <ListItemIcon><Collections fontSize="small" /></ListItemIcon>
          <ListItemText>Choose from library</ListItemText>
        </MenuItem>
      </Menu>

      <ServerImagePickerDialog
        open={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        onSelect={(file) => handleLibrarySelect(file.id)}
      />

      <ChatReferencePickerDialog
        open={refPickerOpen}
        onClose={() => setRefPickerOpen(false)}
        onPick={(ref, label) =>
          setReferences((cur) =>
            // Picking the same thing twice is a slip, not an instruction.
            cur.some((c) => c.ref.id === ref.id && c.ref.kind === ref.kind)
              ? cur
              : [...cur, { ref, label }]
          )
        }
      />

      {/* Emoji Picker Popover */}
      <Popover
        open={Boolean(emojiAnchor)}
        anchorEl={emojiAnchor}
        onClose={() => setEmojiAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 1, display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 0.5, maxWidth: 280 }}>
          {EMOJI_LIST.map((emoji) => (
            <IconButton
              key={emoji}
              size="small"
              onClick={() => handleEmojiClick(emoji)}
              sx={{ fontSize: '1.2rem', '&:hover': { backgroundColor: 'primary.light' } }}
            >
              {emoji}
            </IconButton>
          ))}
        </Box>
      </Popover>
    </Box>
  )
}
